import {Trans} from '@lingui/macro';
import React, {FC, ReactNode} from 'react';
import styled, {css} from 'styled-components';

import {Handler} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ChromelessButton} from '../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {IconButton} from '../../../libs/shared/ui/src/components/buttons/iconButton';
import {Icon} from '../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../libs/shared/ui/src/styles/fontStyles';
import {GridAreaItem} from '../../components/common/grid';

/*
 * Props.
 */

interface SettingsSidebarInfoBoxProps {
  className?: string;
  title: ReactNode;
  body: ReactNode;
  onCloseClick?: Handler;
  onFooterClick?: Handler;
  footer?: ReactNode;
}

/*
 * Style.
 */

const StyledInfoBoxDiv = styled.div<{$hasFooter: boolean}>`
  flex-shrink: 0;
  position: relative;
  display: grid;
  grid-template-areas:
    'icon . title'
    'body body body'
    '. . footer';
  grid-template-columns: 20px 10px 1fr;
  grid-template-rows: repeat(3, auto);
  grid-row-gap: 8px;
  width: 248px;
  border-radius: 16px;
  background: ${(p) => p.theme.greys.shade20};
  padding: 17px 20px 10px 17px;

  ${(p) =>
    !p.$hasFooter &&
    css`
      grid-template-rows: repeat(2, auto);
      grid-template-areas:
        'icon . title'
        'body body body';
    `}
`;

const StyledTitleDiv = styled(GridAreaItem).attrs({
  area: 'title',
})`
  color: ${(p) => p.theme.greys.shade80};
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.semibold};
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const StyledBodyDiv = styled(GridAreaItem).attrs({
  area: 'body',
})`
  color: ${(p) => p.theme.greys.shade80};
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.normal};
  line-height: 22px;

  ul,
  ol {
    padding: 0;
    margin-top: 0;
    margin-bottom: 0;
    padding-left: 1.65em;

    li {
      padding-left: 6px;
    }
  }

  p {
    padding-left: 2.2em;
    margin: 5px 0;

    &:first-child {
      margin-top: 0;
    }
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const StyledFooterDiv = styled(GridAreaItem).attrs({
  area: 'footer',
})`
  display: flex;
`;

const StyledIconWrapper = styled(ChromelessButton)`
  grid-area: icon;
  color: ${(p) => p.theme.greys.shade80};
`;

const StyledButtonContentDiv = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: auto;
  grid-column-gap: 4px;
`;

const StyledButton = styled(Button)`
  margin-left: -12px;
`;

const StyledCloseButtonWrapper = styled.div`
  position: absolute;
  right: 5px;
  top: 5px;
`;

/*
 * Component.
 */

export const SettingsSidebarInfoBox: FC<SettingsSidebarInfoBoxProps> = (props) => (
  <StyledInfoBoxDiv className={props.className} $hasFooter={Boolean(props.onFooterClick && props.footer)}>
    {props.onCloseClick && (
      <StyledCloseButtonWrapper>
        <IconButton
          label={<Trans id="settings-info-box-close">Close</Trans>}
          onClick={props.onCloseClick}
          iconName="closeSmall"
        />
      </StyledCloseButtonWrapper>
    )}
    <StyledIconWrapper>
      <Icon name="info" />
    </StyledIconWrapper>
    <StyledTitleDiv>{props.title}</StyledTitleDiv>
    <StyledBodyDiv>{props.body}</StyledBodyDiv>
    {props.onFooterClick && props.footer ? (
      <StyledFooterDiv>
        <StyledButton
          size={VisualSizesEnum.MEDIUM}
          variant={ButtonVariantsEnum.LINK}
          onClick={props.onFooterClick}
        >
          <StyledButtonContentDiv>
            {props.footer}
            <Icon name="externalLinkSmall" />
          </StyledButtonContentDiv>
        </StyledButton>
      </StyledFooterDiv>
    ) : null}
  </StyledInfoBoxDiv>
);
